'use client';

import { useEffect, useState, useContext, type FC } from 'react';
import { Button, ButtonGroup, Dialog, Flex, Input } from '@/_components';
import {
  saveQuestionnaires,
  saveQuestionnaire,
} from '@/_api/admin-questionnaires';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { validateDueDate } from '@/_utils/validations';
import type { EditDueDateDialogProps } from './EditDueDateDialog.types';
import {
  getNDaysAfter,
  getInputFormattedDateForNDaysFromNow,
} from '@/_utils/dates';

const TODAYS_DATE: string = getNDaysAfter(0);

export const EditDueDateDialog: FC<EditDueDateDialogProps> = ({
  isOpen,
  loadQuestionnaires,
  onClose,
  selectedCompanyId,
  selectedQuestionnaires,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const [isLoading, setIsLoading] = useState(false);
  const [dueDate, setDueDate] = useState(
    getInputFormattedDateForNDaysFromNow(7),
  );
  const [hasUserModifiedDate, setHasUserModifiedDate] = useState(false);

  const onSave = async () => {
    if (!validateDueDate(dueDate)) {
      addToast('error', 'Please select a valid due date');
      return;
    }

    try {
      setIsLoading(true);

      const isSingleQuestionnaire: boolean =
        selectedQuestionnaires?.length === 1;

      if (isSingleQuestionnaire) {
        await saveQuestionnaire({
          company_id: selectedCompanyId!,
          reminders_disabled: false,
          due_date: dueDate,
          id: selectedQuestionnaires[0]?.id!,
          token,
        });
      } else {
        await saveQuestionnaires({
          due_date: dueDate,
          reminders_disabled: false,
          questionnaire_ids: selectedQuestionnaires
            .filter((q) => !q.locked)
            .map((q) => q.id),
          token,
        });
      }

      await loadQuestionnaires();

      addToast('success', 'Due date successfully changed');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
      setHasUserModifiedDate(false);
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      setHasUserModifiedDate(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const isSingle: boolean = selectedQuestionnaires?.length === 1;
    const currentDueDate: string | undefined | null =
      selectedQuestionnaires?.[0]?.due_date;

    // hasUserModifiedDate is used to prevent the due date from being reset
    if (isSingle && currentDueDate && isOpen && !hasUserModifiedDate) {
      setDueDate(currentDueDate);
    }
  }, [selectedQuestionnaires, isOpen, hasUserModifiedDate]);

  const handleClose = () => {
    setHasUserModifiedDate(false);
    onClose();
  };

  return (
    <Dialog
      isNarrow
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Due Dates"
    >
      <Dialog.Content>
        <Flex isColumn={true} gap={6}>
          <Input
            label="Due Date"
            min={TODAYS_DATE}
            type="date"
            isDisabled={isLoading}
            value={dueDate}
            onChange={(e) => {
              setDueDate(e.target.value);
              setHasUserModifiedDate(true);
            }}
          />

          <ButtonGroup align="right">
            <Button
              intent="secondary"
              isDisabled={isLoading}
              onClick={handleClose}
              value="Cancel"
            />
            <Button
              intent="primary"
              isDisabled={isLoading}
              onClick={onSave}
              value="Save"
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
